/* eslint-disable no-case-declarations */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import genericStyles from "../../Styles/genericStyles.module.css";
import styles from "../../Styles/task.module.css";
import Config from "../../Utils/Config";
import { SubtaskColumns, TablesIds } from "../../Utils/Constants";
import {
  EComponentTypes,
  EPriorityColors,
  ETableEditAction,
  ETaskPriority,
  ETaskType,
} from "../../Utils/Enums";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { checkIfTaskIsLate, formatStringDate } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import { ContentCopy, Delete, Edit } from "@mui/icons-material";
import { AxiosResponse } from "axios";
import { getData } from "../../Services/getData";
import { Fragment, useContext, useEffect, useState } from "react";
import GenericModal from "../GenericModal";
import { TaskContext } from "../../Context/TaskContext";
import { DrawerStateContext } from "../../Context/DrawerStateContext";
import { deleteData } from "../../Services/deleteData";
import { RefreshDataContext } from "../../Context/RefreshDataContext";
import { useLocation } from "react-router-dom";
import Filters from "../Widgets/Filters";
import * as yup from "yup";
import { Filter } from "../../Utils/Types";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { TaskModel } from "../../Utils/Models";

export default function TasksTable() {
  const [triggerReload, setTriggerReload]: any = useState();
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState(false);
  const [selectedID, setSelectedID] = useState();
  const drawerStateContext = useContext(DrawerStateContext);
  const refreshContext = useContext(RefreshDataContext);
  const taskContext = useContext(TaskContext);
  const location = useLocation();
  const memo = useCustomMemo();
  const taskStatuses = memo?.storage.taskStatuses || [];
  const taskPeriodicities = memo?.storage.taskPeriodicities || [];
  const priorityColors: any = {
    [ETaskPriority.HIGH]: EPriorityColors.HIGH,
    [ETaskPriority.MEDIUM]: EPriorityColors.MEDIUM,
    [ETaskPriority.LOW]: EPriorityColors.LOW,
  };

  const filters: Filter[] = [
    {
      property: "start",
      label: Vocabulary.start,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
      ).toISOString(),
    },
    {
      property: "end",
      label: Vocabulary.end,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: new Date(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        0
      ).toISOString(),
    },
    {
      property: "status",
      label: Vocabulary.status,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: taskStatuses,
      autocompleteFilterProperty: "_id",
    },
    {
      property: "type",
      label: Vocabulary.type,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: Object.values(ETaskType),
      autocompleteFilterProperty: "value",
      disableFlag: true,
      controlProperty: "lateTask",
    },
    {
      property: "periodicity",
      label: Vocabulary.periodicity,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: taskPeriodicities,
      autocompleteFilterProperty: "_id",
    },
    {
      property: "priority",
      label: Vocabulary.priority,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: Object.values(ETaskPriority),
      autocompleteFilterProperty: "value",
    },
    {
      property: "lateTask",
      label: Vocabulary.lateTask,
      type: EComponentTypes.CHECKBOX,
      validation: yup.mixed().nullable(),
      defaultValue: false,
      autocompleteFilterProperty: "lateTask",
    },
  ];

  const tasksColumns = [
    {
      label: Vocabulary.priority,
      name: "priority",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return (
            <div className={styles.taskHeaderContainer}>
              <div
                className={styles.priorityContainer}
                style={{
                  backgroundColor: priorityColors[value],
                }}
              />
            </div>
          );
        },
      },
    },
    {
      label: Vocabulary.fancyName,
      name: "text",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return `${value.length >= 40 ? `${value?.substring(0, 40)}...` : value
            }`;
        },
      },
    },
    {
      label: Vocabulary.assignedUser,
      name: "assignedUsers",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return value?.map((user: any) => {
            return `${user?.firstName || ""} ${user?.lastName || ""}; `;
          });
        },
      },
    },
    {
      label: Vocabulary.status,
      name: "status",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return value?.name;
        },
      },
    },
    {
      label: Vocabulary.periodicity,
      name: "periodicity",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return value?.name;
        },
      },
    },
    {
      label: Vocabulary.type,
      name: "type",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) => {
          return value;
        },
      },
    },
    {
      label: Vocabulary.startDate,
      name: "start",
      options: {
        sort: true,
        customBodyRender: (value: any, meta: any) => {
          return formatStringDate(value, Config.momentEUDateFormat);
        },
      },
    },
    {
      label: Vocabulary.endDate,
      name: "end",
      options: {
        sort: true,
        customBodyRender: (value: any, meta: any) => {
          return formatStringDate(value, Config.momentEUDateFormat);
        },
      },
    },
    {
      label: "subtasks",
      name: "subtasks",
      options: { display: false, viewColumns: false },
    },
    {
      label: Vocabulary.delay,
      name: "delay",
      options: {
        sort: false,
        customBodyRender: (value: any, meta: any) =>
          value ? `${value} ${Vocabulary.days}` : "-",
      },
    },
    {
      label: "createdBy",
      name: "createdBy",
      options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false,
      },
    },
  ];

  /**
   * Refresh the table when the refresh context is changed
   */
  useEffect(() => {
    if (
      refreshContext.refresh &&
      location.pathname.includes(localUrlEnum.tasks)
    ) {
      setTriggerReload(!triggerReload);
      refreshContext.setRefresh(false);
    }
  }, [refreshContext.refresh]);

  /**
   *
   * @param task
   */
  const duplicateTask = (id: string) => {
    if (!id) return;
    getData(`${endpoints.task.duplicate}/${id}`).then((res: AxiosResponse) => {
      if (!res || !res.data) return;
      setTriggerReload(!triggerReload);
    });
  };

  /**
   *
   */
  const toggleDeleteModalState = () => {
    setOpenDeleteTaskModal(!openDeleteTaskModal);
  };

  /**
   *
   * @param id
   */
  const deleteTask = () => {
    if (!selectedID) return;
    deleteData(`${endpoints.task.main}/${selectedID}`)
      .then((res: any) => {
        toggleDeleteModalState();
        if (res) {
          setTriggerReload(!triggerReload);
        }
      })
      .catch((ex) => {
        console.log(ex);
      });
  };

  /**
   *
   * @param id
   */
  const handleEditTask = (task: any) => {
    taskContext.setTaskId(task?._id);
  };

  /**
   *
   * @param id
   */
  const getTask = (id: string | null) => {
    if (id) taskContext.setTaskId(id);
  };

  /**
   *
   */
  const addNewTask = () => {
    drawerStateContext.setOpen(true);
  };

  /**
   *
   * @param data
   * @param dataIndex
   * @param rowIndex
   * @returns
   */
  const renderCustomRow = (
    data: any,
    dataIndex: any,
    rowIndex: any,
    tableData: any[]
  ) => {
    return (
      <Fragment key={dataIndex}>
        <TableRow
          onClick={() => {
            getTask(tableData.length ? tableData[rowIndex].id : null);
          }}
          className={`${checkIfTaskIsLate(tableData[rowIndex]) ? styles.lateTask : ""
            }`}
        >
          {data?.map((element: any, key: any) => {
            return renderColumnCustom(element, key, rowIndex, tableData);
          })}
        </TableRow>
        {data[8] && data[8].length ? (
          <Fragment key={rowIndex}>
            <tr>
              <td colSpan={10} className={styles.unassignedTasksTd}>
                <TableContainer>
                  <Table className={genericStyles.fullWidthElement}>
                    <TableHead>
                      <TableRow className={styles.unassignedTasksTableRow}>
                        {SubtaskColumns?.map((value: any, index: any) => (
                          <TableCell key={index}>{value.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody className={styles.subtask}>
                      {data[8]?.map((row: any, dataIndex: any) => (
                        <TableRow key={dataIndex}>
                          {SubtaskColumns?.map((column: any, index: any) => (
                            <TableCell key={index} component="th" scope="row">
                              {row[column.name]?.name ||
                                row[column.name] ||
                                index + 1}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </td>
            </tr>
          </Fragment>
        ) : null}
      </Fragment>
    );
  };

  /**
   *
   * @param element
   * @param key
   * @param rowIndex
   * @returns
   */
  const renderColumnCustom = (
    element: any,
    key: any,
    rowIndex: any,
    tableData: any[]
  ) => {
    switch (key) {
      case 1:
        return (
          <TableCell key={key} align="left">
            {element}
            <p id={`specificParagraph${rowIndex}`}></p>
          </TableCell>
        );
      case 2:
        return (
          <TableCell key={key} align="left">
            {element}
          </TableCell>
        );
      case 8:
      case 10:
        return null;
      case 11:
        return (
          <TableCell key={key} align="center">
            <ToggleButtonGroup exclusive aria-label="text alignment">
              <Tooltip
                title={Vocabulary.edit}
                className={genericStyles.tooltipEditButton}
              >
                <ToggleButton
                  value="left"
                  aria-label="left aligned"
                  onClick={(event: any) => {
                    event.stopPropagation();
                    handleEditTask(tableData[rowIndex]);
                  }}
                >
                  <Edit />
                </ToggleButton>
              </Tooltip>

              <Tooltip
                title={Vocabulary.DUPLICATE}
                className={genericStyles.tooltipDuplicateButton}
              >
                <ToggleButton
                  value="left"
                  aria-label="left aligned"
                  onClick={(event: any) => {
                    event.stopPropagation();
                    duplicateTask(tableData[rowIndex]._id);
                  }}
                >
                  <ContentCopy />
                </ToggleButton>
              </Tooltip>
              <Tooltip
                title={Vocabulary.delete}
                className={genericStyles.tooltipDeleteButton}
              >
                <ToggleButton
                  value="center"
                  aria-label="centered"
                  onClick={(event: any) => {
                    event.stopPropagation();
                    setSelectedID(tableData[rowIndex]._id);
                    toggleDeleteModalState();
                  }}
                >
                  <Delete />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </TableCell>
        );
      default:
        return (
          <TableCell key={key} align="left">
            {element}
          </TableCell>
        );
    }
  };

  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} customFilterModel={new TaskModel()} />
      <MUIDataTableCustomStyle
        title={Vocabulary.tasks}
        CRUDEndPoint={endpoints.task.main}
        id={TablesIds.tasks}
        columns={tasksColumns}
        triggerReload={triggerReload}
        onEditAction={ETableEditAction.MODAL}
        customRowRender={renderCustomRow}
        handleModalForm={addNewTask}
      />
      <GenericModal
        open={openDeleteTaskModal}
        onClose={() => {
          toggleDeleteModalState();
        }}
        title={Vocabulary.confirm}
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              color="secondary"
              onClick={() => {
                toggleDeleteModalState();
              }}
              variant="outlined"
              fullWidth
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                deleteTask();
              }}
            >
              {Vocabulary.delete}
            </Button>
          </div>
        }
      >
        {Vocabulary.confirmDeleteMessage}
      </GenericModal>
    </div>
  );
}
